import type { User } from '@lensora/shared'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Separator,
  Spinner,
} from '@lensora/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'


import { EmailReportsCard } from '@/components/settings/EmailReportsCard'
import { GA4IntegrationCard } from '@/components/settings/GA4IntegrationCard'
import { useMe } from '@/hooks/use-me'
import { api, ApiError } from '@/lib/api'

function useUpdateProfileMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { fullName: string }) => api.patch<User>('/api/auth/me', data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
  })
}

export function SettingsPage() {
  const { data, isLoading } = useMe()
  const [fullName, setFullName] = React.useState('')
  const [saveError, setSaveError] = React.useState<string | null>(null)
  const [justSaved, setJustSaved] = React.useState(false)
  const updateProfile = useUpdateProfileMutation()

  // Populate form once data loads
  React.useEffect(() => {
    if (data?.user.fullName) setFullName(data.user.fullName)
  }, [data?.user.fullName])

  const isDirty = data?.user.fullName !== fullName

  async function handleSave() {
    setSaveError(null)
    setJustSaved(false)
    try {
      await updateProfile.mutateAsync({ fullName: fullName.trim() })
      setJustSaved(true)
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Failed to save changes. Please try again.')
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-text">Settings</h2>
        <p className="mt-1 text-sm text-text-muted">Manage your account preferences.</p>
      </div>

      {/* Profile */}
      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
          <CardDescription>Update your personal details.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={data?.user.email ?? ''} disabled />
            <p className="text-xs text-text-muted">Email cannot be changed here.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="name">Full name</Label>
            <Input
              id="name"
              value={fullName}
              onChange={(e) => {
                setFullName(e.target.value)
                setJustSaved(false)
              }}
              placeholder="Your name"
            />
          </div>
          {saveError && <p className="text-sm text-feedback-error">{saveError}</p>}
        </CardContent>
        <CardFooter className="flex items-center gap-3">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!isDirty || !fullName.trim() || updateProfile.isPending}
          >
            {updateProfile.isPending ? 'Saving…' : 'Save changes'}
          </Button>
          {justSaved && !isDirty && (
            <span className="text-sm text-feedback-success">Saved.</span>
          )}
        </CardFooter>
      </Card>

      <EmailReportsCard />

      <GA4IntegrationCard />

      <Separator />

      {/* Danger zone */}
      <Card className="border-feedback-error/30">
        <CardHeader>
          <CardTitle className="text-feedback-error">Danger zone</CardTitle>
          <CardDescription>Irreversible actions for your account.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center gap-3">
            <Button variant="destructive" size="sm" disabled>
              Delete account
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a href={`mailto:hello@lensora.io?subject=${encodeURIComponent('Account deletion request')}&body=${encodeURIComponent(`Please close my account.\n\nEmail: ${data?.user.email ?? ''}`)}`}>
                Contact support
              </a>
            </Button>
          </div>
          <p className="text-xs text-text-muted">
            Account deletion isn't self-service yet — email us and we'll take care of it.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
